import React from "react";
import { formatCurrency, formatNumber } from "../utils/numberFormatters";
import { ShowStatus } from "@quantizr-engineering/common/types/show";
import { getStatusStyling } from "../utils/showStatus";
import Tooltip from "./Tooltip";
import { FiHelpCircle } from "react-icons/fi";

interface SummaryCardProps {
  walkout?: number;
  walkoutMinimum?: number;
  walkoutPotential?: number;
  status: ShowStatus;
  boxOfficeScore?: number;
  showScore?: number;
  description2?: string;
  ticketsSold?: number;
  sellableCap?: number;
  statusClass?: string;
  walkoutTooltipText?: string;
  qzScoreTooltipText?: string;
  boxOfficeTooltipText?: string;
  ticketsSoldTooltipText?: string;
}

const ValuesCard: React.FC<SummaryCardProps> = ({
  walkout,
  walkoutMinimum,
  walkoutPotential,
  status,
  boxOfficeScore,
  showScore,
  description2,
  ticketsSold,
  sellableCap,
  statusClass,
  walkoutTooltipText,
  qzScoreTooltipText,
  boxOfficeTooltipText,
  ticketsSoldTooltipText,
}) => {
  return (
    <div
      className={`
        col-span-6 
        ${statusClass || ""} 
        rounded-lg 
        pb-8 
        sm:pb-10 
        h-[240px] 
        flex
        flex-col
      `}
    >
      <div className="flex-1 flex flex-col justify-between mx-4 mt-4">
        <div>
          <div className="flex justify-between mr-8">
            <div>
              <div className="flex flex-row items-center gap-2">
                <label className="text-sm font-medium text-gray-700">
                  Walkout
                </label>
                <Tooltip content={walkoutTooltipText}>
                  <span className="text-xs text-blue-900">
                    <FiHelpCircle />
                  </span>
                </Tooltip>
              </div>
              <span className="text-black text-xl sm:text-xl font-bold">
                {walkout !== undefined ? formatCurrency(walkout) : "-"}
              </span>
              {(!!walkoutMinimum || !!walkoutPotential) && (
                <div className="text-black text-sm sm:text-sm">
                  {!!walkoutMinimum !== undefined
                    ? "$" + formatNumber(walkoutMinimum ?? 0, 0)
                    : "-"}{" "}
                  -{" "}
                  {!!walkoutPotential !== undefined
                    ? "$" + formatNumber(walkoutPotential ?? 0, 0)
                    : "-"}
                </div>
              )}
            </div>
            <div>
              <div className="flex flex-row items-center gap-2">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  Qz Score
                </label>
                <Tooltip content={qzScoreTooltipText}>
                  <span className="text-xs text-blue-900">
                    <FiHelpCircle />
                  </span>
                </Tooltip>
              </div>
              <span className="text-black text-lg sm:text-lg font-bold">
                {showScore !== undefined ? formatNumber(showScore) : "-"}
              </span>
            </div>
          </div>
        </div>
        <div className="mt-auto">
          <div className="flex flex-row items-center gap-2">
            <label className="text-sm font-medium text-gray-700">
              Box Office Score
            </label>
              <Tooltip content={boxOfficeTooltipText}>
                <span className="text-xs text-blue-900">
                  <FiHelpCircle />
                </span>
              </Tooltip>
          </div>
        </div>

        <span className="text-black text-lg text-left sm:text-lg font-bold">
          {boxOfficeScore !== undefined ? boxOfficeScore + "%" : "-"}
        </span>

        <div className="flex flex-row items-center gap-2 mt-2">
          <label className="text-sm text-[#48455B]">Tickets Sold / Cap</label>
            <Tooltip content={ticketsSoldTooltipText}>
              <span className="text-xs text-blue-900">
                <FiHelpCircle />
              </span>
            </Tooltip>
        </div>

        {(ticketsSold !== undefined ||
          sellableCap !== undefined ||
          description2) && (
          <div className="flex flex-row justify-between">
            {(ticketsSold !== undefined || sellableCap !== undefined) && (
              <div className="text-black text-sm sm:text-sm">
                {ticketsSold !== undefined ? ticketsSold : "-"} /{" "}
                {sellableCap !== undefined ? sellableCap : "-"}
              </div>
            )}
            <label
              className={`text-lg font-medium ${getStatusStyling(status)}`}
            >
              <b>
                {status === ShowStatus.DRAFT
                  ? "Potential"
                  : status === ShowStatus.CONFIRMED
                  ? "Projected"
                  : "Actual"}
              </b>
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

export default ValuesCard;
